import { AbstractControl, FormGroupDirective, NgForm } from '@angular/forms';

/**
 * Interface for determining when a form control should display an error state. Injectable to provide custom error
 * display logic.
 *
 * @example
 *   // Use with HxFormGroup
 *   <hx-form-group [errorStateMatcher]="customMatcher">
 *   <hx-label>Email</hx-label>
 *   <input type="email" [formControl]="emailControl" />
 *   </hx-form-group>
 */
export type ErrorStateMatcher = {
  /**
   * Determines if a control should be considered in an error state.
   *
   * @param control - The form control to check (FormControl, FormControlName, NgModel)
   * @param form - The parent form (FormGroupDirective or NgForm)
   * @returns True if control should show error state
   */
  isErrorState(control: AbstractControl | null, form: FormGroupDirective | NgForm | null): boolean;
};

/**
 * Default error state matcher - shows errors when invalid AND (touched OR dirty) Matches Angular Material default
 * behavior.
 *
 * @example
 *   // Default behavior (no need to specify)
 *   <hx-form-group>
 *   <input [formControl]="emailControl" />
 *   </hx-form-group>
 *   // Shows errors after user touches field or modifies value
 */
export class DefaultErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: AbstractControl | null): boolean {
    return !!(control?.invalid && (control?.touched || control?.dirty));
  }
}

/**
 * Immediate error state matcher - shows errors immediately when invalid Useful for real-time validation (e.g., password
 * strength indicators).
 *
 * @example
 *   // Show errors immediately as user types
 *   readonly immediateErrorMatcher = new ImmediateErrorStateMatcher();
 *
 *   <hx-form-group [errorStateMatcher]="immediateErrorMatcher">
 *   <input type="password" [formControl]="passwordControl" />
 *   </hx-form-group>
 */
export class ImmediateErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: AbstractControl | null): boolean {
    return !!control?.invalid;
  }
}

/**
 * Show-on-dirty error state matcher - shows errors only when modified (ignore touched) Useful for forms with prefilled
 * values where touch shouldn't trigger errors.
 *
 * @example
 *   // Show errors only after user modifies field
 *   readonly dirtyErrorMatcher = new ShowOnDirtyErrorStateMatcher();
 *
 *   <hx-form-group [errorStateMatcher]="dirtyErrorMatcher">
 *   <input [formControl]="prefilled Control" />
 *   </hx-form-group>
 */
export class ShowOnDirtyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: AbstractControl | null): boolean {
    return !!(control?.invalid && control?.dirty);
  }
}

/**
 * Parent-form-submitted error state matcher - shows errors only after form submission Prevents error spam in
 * multi-field forms until user attempts to submit.
 *
 * @example
 *   // Show errors only after form submission attempt
 *   readonly submittedErrorMatcher = new ParentFormSubmittedErrorStateMatcher();
 *
 *   <form [formGroup]="myForm" (ngSubmit)="onSubmit()">
 *   <hx-form-group [errorStateMatcher]="submittedErrorMatcher">
 *   <input formControlName="email" />
 *   </hx-form-group>
 *   </form>
 */
export class ParentFormSubmittedErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: AbstractControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return !!(control?.invalid && (form?.submitted || control?.touched));
  }
}
